type ViewRequest = { status: 'idle'; revision: number } | { status: 'loading'; revision: number };

/** Serialize each layer's reads and reject results invalidated by a newer view or source. */
export class DataLayerViewLoader<Source, Result> {
  private readonly requests = new Map<string, ViewRequest>();
  private state: 'active' | 'disposed' = 'active';

  constructor(private readonly deps: {
    read(id: string): Source | undefined;
    load(source: Source): Promise<Result>;
    current(id: string, source: Source): boolean;
    apply(id: string, source: Source, result: Result): void;
    failed(id: string, error: Error): void;
  }) {}

  async request(id: string): Promise<void> {
    if (this.state === 'disposed') return;
    const previous = this.requests.get(id);
    const revision = (previous?.revision ?? 0) + 1;
    if (previous?.status === 'loading') { previous.revision = revision; return; }
    const request: ViewRequest = { status: 'loading', revision };
    this.requests.set(id, request);
    try {
      let requested: number;
      do {
        requested = request.revision;
        const source = this.deps.read(id);
        if (source === undefined) break;
        let result: Result;
        try { result = await this.deps.load(source); }
        catch (error) {
          if (!this.owns(id, request)) break;
          if (requested !== request.revision || !this.deps.current(id, source)) continue;
          this.deps.failed(id, error instanceof Error ? error : new Error('Local dataset view failed', { cause: error }));
          break;
        }
        if (!this.owns(id, request)) break;
        if (requested !== request.revision || !this.deps.current(id, source)) continue;
        this.deps.apply(id, source, result);
      } while (requested !== request.revision);
    } catch (error) {
      if (this.owns(id, request)) this.deps.failed(id, error instanceof Error ? error : new Error('Local dataset view failed', { cause: error }));
    } finally {
      if (this.requests.get(id) === request) this.requests.set(id, { status: 'idle', revision: request.revision });
    }
  }

  private owns(id: string, request: ViewRequest): boolean {
    return this.state === 'active' && this.requests.get(id) === request;
  }

  invalidate(id: string): void {
    const request = this.requests.get(id);
    if (request) request.revision += 1;
  }

  dispose(): void {
    this.state = 'disposed';
    this.requests.clear();
  }
}
